import React, { useState, useEffect, useRef, useCallback, useContext } from "react";
import CountrySelector from '../components/CountrySelector'
import { doE164, formatPhoneNumber, getCallMode } from "../components/Utils";
import NumberPad from "../components/NumberPad";
import { ReactSipPhone, phoneStore } from "react-sip-phone";
import { isEmpty } from "lodash";
import "react-sip-phone/dist/index.css";
import "../Dialler/DiallerKeyPad.css";
import { useSelector, useDispatch } from "react-redux";
import { resetCallInfo } from '../../../state/actions/phone'
import Config from "../../../utils/config/config";
import countryCodes from "../components/CountryList";
import { fetchWalletBalanceService, getLoggedInUserCallHistory } from "../../../state/actions/user";
import { UserServiceClientApi } from "../../../services/api/UserApiServices";
import { RechargeContext } from "../../../RechargeContext";
import { showToast } from "../../../components/ui/toaster";
import webSocketApiService from "../../../services/socket/wsApiServices";
import { isUserOnline } from "../../../services/storage/LocalStorage";

const sipuri = Config.sipURI;
const password = Config.sipPassword;
const websocket = Config.sipWebsocket;
const name = "";
const disabledButtons = [
	"numpad",
	"transfer",
];
const disabledFeatures = ["dialstring", "settings", "remoteid"];
const mode = "";
const RECOVERY_TIMEOUT_MS = 5000;

const DiallerKeyPad = ({
	callerInfo,
	phoneNumFromLink,
	onClose,
	noBalance,
}) => {
	const [country, setCountry] = useState(null);
	const [fullPhoneNumber, setFullPhoneNumber] = useState("+");
	const [phoneState, setPhoneState] = useState(null);
	const [duration, setDuration] = useState(0);
	const [showNumberPlate, setShowNumberPlate] = useState(true);
	const [callTriggered, setCallTriggered] = useState(false);
	const [callStartTime, setCallStartTime] = useState(null);
	const [isInitialized, setIsInitialized] = useState(false);
	const reduxState = useSelector(state => state.user)
	const { openRecharge } = useContext(RechargeContext);

	const user = useSelector((state) => state.user);
	const selectedDomain = useSelector((state) => state.domain.selectedDomain);
	const dispatch = useDispatch();
	const phnInput = useRef(null);
	const cursor = useRef(0);
	const phoneStateInterval = useRef(null);
	const recoveryPendingRef = useRef(false);
	const recoveryInProgressRef = useRef(false);
	const lastRecoveryAttemptRef = useRef(0);
	const recoveryDeadlineRef = useRef(null);
	const recoveryDropTriggeredRef = useRef(false);
	const offlineDropTimeoutRef = useRef(null);

	// Add refs to track API call states to prevent duplicates
	const apiCallStates = useRef({
		callStartSent: false,
		callEndSent: false,
		callSummarySent: false,
		lastProcessedState: null,
		callStartTimestamp: null,
		callDurationStart: null
	});

	const setCursorPositions = useCallback(() => {
		// Use setTimeout to ensure DOM is ready
		setTimeout(() => {
			if (phnInput.current) {
				try {
					phnInput.current.setSelectionRange(cursor.current, cursor.current);
				} catch (error) {
					console.warn('Could not set cursor position:', error);
				}
			}
		}, 0);
	}, []);

	// Helper function to find country by dial code
	const findCountryByDialCode = useCallback((number) => {
		if (!number || number.length < 2) return null;

		try {
			// Sort countries by dial code length (longest first) to match most specific codes
			const sortedCountries = [...countryCodes].sort((a, b) => b.dial_code.length - a.dial_code.length);

			for (let country of sortedCountries) {
				if (number.startsWith(country.dial_code)) {
					return country;
				}
			}
		} catch (error) {
			console.error('Error finding country by dial code:', error);
		}
		return null;
	}, []);

	// Helper function to clean and validate phone number input
	const cleanPhoneNumber = useCallback((input) => {
		try {
			// Remove all non-numeric characters except +
			let cleaned = input.replace(/[^\d+]/g, '');

			// Ensure it starts with +
			if (!cleaned.startsWith('+')) {
				if (cleaned.length > 0) {
					cleaned = '+' + cleaned;
				} else {
					cleaned = '+';
				}
			}

			// Remove any + that are not at the beginning
			cleaned = '+' + cleaned.substring(1).replace(/\+/g, '');

			return cleaned;
		} catch (error) {
			console.error('Error cleaning phone number:', error);
			return '+';
		}
	}, []);

	const selectCountry = useCallback((selectedCountry) => {
		try {
			setCountry(selectedCountry);
			const formattedNumber = formatPhoneNumber(selectedCountry.dial_code, null);
			setFullPhoneNumber(formattedNumber);
			cursor.current = selectedCountry.dial_code.length;
		} catch (error) {
			console.error('Error selecting country:', error);
		}
	}, []);

	const addNumber = useCallback((phoneNumber) => {
		try {
			let newNumber;

			if (fullPhoneNumber === "+") {
				newNumber = "+" + phoneNumber;
			} else {
				// Insert number at cursor position
				const beforeCursor = fullPhoneNumber.substring(0, cursor.current);
				const afterCursor = fullPhoneNumber.substring(cursor.current);
				newNumber = beforeCursor + phoneNumber + afterCursor;
			}

			// Clean the number
			newNumber = cleanPhoneNumber(newNumber);

			// Find and set country
			const foundCountry = findCountryByDialCode(newNumber);
			if (foundCountry && foundCountry !== country) {
				setCountry(foundCountry);
			}

			// Format and set the number
			const formattedNumber = formatPhoneNumber(null, newNumber);
			setFullPhoneNumber(formattedNumber);

			// Update cursor position
			cursor.current = Math.min(cursor.current + 1, formattedNumber.length);
		} catch (error) {
			console.error('Error adding number:', error);
		}
	}, [fullPhoneNumber, country, cleanPhoneNumber, findCountryByDialCode]);

	const handlePhoneNumberChange = useCallback((e) => {
		try {
			let val = e.target.value;
			const cursorPosition = e.target.selectionStart;
			cursor.current = cursorPosition;

			// Clean the input
			const cleanedVal = cleanPhoneNumber(val);

			// If user deleted everything, reset to +
			if (cleanedVal.length <= 1) {
				setFullPhoneNumber("+");
				setCountry(null);
				cursor.current = 1;
				return;
			}

			// Find country based on the cleaned number
			const foundCountry = findCountryByDialCode(cleanedVal);
			if (foundCountry && foundCountry !== country) {
				setCountry(foundCountry);
			} else if (!foundCountry && country) {
				// If no country found but we had one before, keep it if the number still starts with its dial code
				if (!cleanedVal.startsWith(country.dial_code)) {
					setCountry(null);
				}
			}

			// Format and set the number
			const formattedNumber = formatPhoneNumber(null, cleanedVal);
			setFullPhoneNumber(formattedNumber);
		} catch (error) {
			console.error('Error handling phone number change:', error);
		}
	}, [cleanPhoneNumber, findCountryByDialCode, country]);

	// Handle backspace/delete more intelligently
	const handleKeyDown = useCallback((e) => {
		try {
			if (e.key === 'Backspace' || e.key === 'Delete') {
				const cursorPosition = e.target.selectionStart;
				cursor.current = cursorPosition;

				// If we're at position 1 (right after +), prevent deletion
				if (cursorPosition <= 1 && e.key === 'Backspace') {
					e.preventDefault();
					return;
				}
			}
		} catch (error) {
			console.error('Error handling key down:', error);
		}
	}, []);

	const resetCallStates = useCallback(() => {
		try {
			setPhoneState(null);
			setDuration(0);
			setCallStartTime(null);
			setFullPhoneNumber("+");
			setCountry(null);
			setShowNumberPlate(true);
			setCallTriggered(false);

			// Reset API call tracking
			apiCallStates.current = {
				callStartSent: false,
				callEndSent: false,
				callSummarySent: false,
				lastProcessedState: null,
				callStartTimestamp: null,
				callDurationStart: null
			};

			// Clear interval if exists
				if (phoneStateInterval.current) {
					clearInterval(phoneStateInterval.current);
					phoneStateInterval.current = null;
				}
				if (offlineDropTimeoutRef.current) {
					clearTimeout(offlineDropTimeoutRef.current);
					offlineDropTimeoutRef.current = null;
				}
					recoveryPendingRef.current = false;
					recoveryInProgressRef.current = false;
					lastRecoveryAttemptRef.current = 0;
					recoveryDeadlineRef.current = null;
					recoveryDropTriggeredRef.current = false;

			// Dispatch reset action
			dispatch(resetCallInfo());
		} catch (error) {
			console.error('Error resetting call states:', error);
		}
		}, [dispatch]);

	const beginRecoveryWindow = useCallback(() => {
		if (!recoveryDeadlineRef.current) {
			recoveryDeadlineRef.current = Date.now() + RECOVERY_TIMEOUT_MS;
		}
	}, []);

	const clearRecoveryWindow = useCallback(() => {
		recoveryDeadlineRef.current = null;
		recoveryDropTriggeredRef.current = false;
	}, []);

	const handleTerminatedCallState = useCallback(async (resolvedNumber = null) => {
		try {
			setPhoneState("Terminated");
			setCallTriggered(false);
			recoveryPendingRef.current = false;
			clearRecoveryWindow();
			apiCallStates.current.lastProcessedState = "Terminated";

			const num = (callerInfo?.toNumber || fullPhoneNumber) || "";
			const number = resolvedNumber || doE164(num);

			if (
				number &&
				user?.user &&
				!apiCallStates.current.callEndSent &&
				!apiCallStates.current.callSummarySent
			) {
				const callObject = {
					userId: number?.formatted?.substring(1),
					callerUserId: user.user.userId,
					videoSessionId: null,
					video: false,
					selectedDomain: selectedDomain
				};

				const isSatCall = getCallMode(number.countryCode) === "sat";

				await webSocketApiService.SendVoipPushNotification({
					...callObject,
					callAction: "CallEnd",
				});
				apiCallStates.current.callEndSent = true;

				const callDuration = apiCallStates.current.callDurationStart
					? (performance.now() - apiCallStates.current.callDurationStart) / 1000
					: 0;

				const callSummaryResponse = await webSocketApiService.SendVoipPushNotification({
					...callObject,
					callAction: "CallSummary",
					callStartTime: apiCallStates.current.callStartTimestamp || callStartTime,
					callDuration: callDuration,
					callType: isSatCall ? "SAT" : "PSTN",
					calledNumber: isSatCall ? "+12124010649" : number.formatted,
					dialledSatPhoneNumber: isSatCall ? number.formatted : "",
					isLocalContact: false,
					showNumberPlate: true,
				});
				if (callSummaryResponse?.success) {
					let now = Date.now();
					dispatch(getLoggedInUserCallHistory(now));
					dispatch(fetchWalletBalanceService());
					apiCallStates.current.callSummarySent = true;
				}
			}

			if (phoneStateInterval.current) {
				clearInterval(phoneStateInterval.current);
				phoneStateInterval.current = null;
			}

			setTimeout(() => {
				resetCallStates();
			}, 1000);
		} catch (error) {
			console.error("Error handling terminated call state:", error);
		}
	}, [callerInfo, fullPhoneNumber, user, selectedDomain, callStartTime, dispatch, resetCallStates, clearRecoveryWindow]);

	const forceDropCallAfterRecoveryFailure = useCallback(async (session) => {
	if (recoveryDropTriggeredRef.current) return;

	recoveryDropTriggeredRef.current = true;
	recoveryPendingRef.current = false;
	recoveryDeadlineRef.current = null;

	console.error("Force terminating call due to network loss");

	try {
		// 1. Kill media first
		destroyPeerConnection(session);

		// 2. Try all SIP termination methods
		if (typeof session?.bye === "function") {
			await session.bye();
		}
		if (typeof session?.terminate === "function") {
			await session.terminate();
		}
		if (typeof session?.cancel === "function") {
			await session.cancel();
		}

		// 3. Force SIP transport disconnect (KEY FIX)
		forceDisconnectSip();

	} catch (err) {
		console.error("Force drop failed:", err);
	} finally {
		showToast('error','Low or no network detected. The call may be disconnected.');
		await handleTerminatedCallState();
		onClose();
	}
}, [handleTerminatedCallState]);

	const checkOnline = useCallback(() => {
		if (!isUserOnline()) {
			const err = new Error("User is offline");
			console.error(err.message);
			return Promise.reject(err);
		}
		return Promise.resolve();
	}, []);

	const getCurrentSession = useCallback(() => {
		const phoneStoreState = phoneStore.getState();
		const sessions = phoneStoreState?.sipSessions?.sessions;
		if (isEmpty(sessions)) return null;
		const sessionKey = Object.keys(sessions)[0];
		return sessions[sessionKey];
	}, []);

	const getSessionState = useCallback((session) => {
		return session?._state || session?.state || null;
	}, []);

	const getConnectionRecoveryFlags = useCallback((peerConnection) => {
		const iceConnectionState = peerConnection?.iceConnectionState;
		const connectionState = peerConnection?.connectionState;
		const signalingState = peerConnection?.signalingState;

		const connectionUnhealthy =
			iceConnectionState === "disconnected" ||
			iceConnectionState === "failed" ||
			connectionState === "disconnected" ||
			connectionState === "failed";

		const signalingStable = signalingState === "stable";
		const hasLiveRemoteAudioTrack = Boolean(
			peerConnection?.getReceivers?.().some(
				(receiver) => receiver?.track?.kind === "audio" && receiver.track.readyState === "live"
			)
		);

		return {
			connectionUnhealthy,
			signalingStable,
			hasLiveRemoteAudioTrack,
		};
	}, []);

	const markRecoveryPendingIfCallActive = useCallback(() => {
			try {
				const session = getCurrentSession();
				const currentState = getSessionState(session);
				if (currentState === "Established" || currentState === "Establishing") {
					recoveryPendingRef.current = true;
					beginRecoveryWindow();
				}
			} catch (error) {
				console.warn("Error marking call recovery pending:", error);
			}
		}, [getCurrentSession, getSessionState, beginRecoveryWindow]);

	const recoverCallMediaAfterReconnect = useCallback(async () => {
		const now = Date.now();
		if (recoveryInProgressRef.current || now - lastRecoveryAttemptRef.current < 1500) {
			return;
		}

		recoveryInProgressRef.current = true;
		lastRecoveryAttemptRef.current = now;
		let recoverySucceeded = false;

		try {
			await checkOnline();

				const session = getCurrentSession();
				const currentState = getSessionState(session);

				if (!session || (currentState !== "Established" && currentState !== "Establishing")) {
					recoveryPendingRef.current = false;
					clearRecoveryWindow();
					return;
				}

				const phoneStoreState = phoneStore.getState();
				const sipAccount = phoneStoreState?.sipAccounts?.sipAccount;
				if (sipAccount?.register) {
					try {
						await sipAccount.register();
					} catch (registerError) {
						console.warn("SIP register during recovery failed:", registerError);
					}
				}

				const peerConnection = session?.sessionDescriptionHandler?.peerConnection;
				const sessionId = session?.id;
				const {
					connectionUnhealthy,
					signalingStable,
					hasLiveRemoteAudioTrack,
				} = getConnectionRecoveryFlags(peerConnection);
				const needsMediaRecovery =
					connectionUnhealthy ||
					(currentState === "Established" && !hasLiveRemoteAudioTrack);

				if (peerConnection && sessionId) {
					const remoteAudioElement = document.getElementById(sessionId);
					if (remoteAudioElement) {
						const remoteStream = new MediaStream();
						peerConnection.getReceivers().forEach((receiver) => {
						if (receiver?.track?.kind === "audio") {
							remoteStream.addTrack(receiver.track);
						}
					});

					if (remoteStream.getTracks().length > 0) {
						remoteAudioElement.srcObject = remoteStream;
						try {
							await remoteAudioElement.play();
							recoverySucceeded = true;
						} catch (playError) {
							console.warn("Remote audio play failed during recovery:", playError);
							}
						}
					}
				}

				// Re-INVITE only after call is truly established (not while still connecting/ringing).
				const canRestartIce =
					currentState === "Established" &&
					apiCallStates.current.callStartSent &&
					!!apiCallStates.current.callStartTimestamp &&
					Date.now() - apiCallStates.current.callStartTimestamp > 2000;

				// Re-INVITE to refresh RTP/ICE after a temporary network outage.
				if (needsMediaRecovery && canRestartIce && typeof session?.invite === "function") {
					if (!signalingStable) {
						console.warn("Skipping SIP re-INVITE recovery while signaling is not stable.");
					} else {
						try {
							await session.invite({
								sessionDescriptionHandlerOptions: {
									iceRestart: true,
								},
							});
							recoverySucceeded = true;
						} catch (inviteError) {
							const inviteErrorMessage = (inviteError?.message || "").toLowerCase();
							const inviteTransactionPending =
								inviteErrorMessage.includes("request pending") ||
								inviteErrorMessage.includes("491") ||
								inviteErrorMessage.includes("glare");

							if (inviteTransactionPending) {
								console.warn("Skipping SIP re-INVITE retry because another SIP transaction is in progress.");
							} else {
								console.warn("SIP re-INVITE recovery failed:", inviteError);
							}
						}
					}
				}

				recoveryPendingRef.current = needsMediaRecovery && !recoverySucceeded;
				if (recoveryPendingRef.current) {
					beginRecoveryWindow();
				} else {
					clearRecoveryWindow();
				}
			} catch (error) {
				console.error("Error recovering call media after reconnect:", error);
				recoveryPendingRef.current = true;
				beginRecoveryWindow();
			} finally {
				recoveryInProgressRef.current = false;
			}
		}, [checkOnline, getCurrentSession, getSessionState, getConnectionRecoveryFlags, beginRecoveryWindow, clearRecoveryWindow]);

	// Improved monitor phone state function with duplicate prevention
	const monitorPhoneState = useCallback(async () => {
		try {
			const phoneStoreState = phoneStore.getState();
			const sessions = phoneStoreState?.sipSessions?.sessions;

			if (!isEmpty(sessions)) {
				const sessionKey = Object.keys(sessions)[0];
				const session = sessions[sessionKey];
				const currentState = getSessionState(session);

				// Only process if state actually changed and we haven't processed this state already
				if (currentState &&
					currentState !== phoneState &&
					currentState !== apiCallStates.current.lastProcessedState) {
					setPhoneState(currentState);
					apiCallStates.current.lastProcessedState = currentState;
					const num = (callerInfo?.toNumber || fullPhoneNumber) || "";
					// Handle state-specific logic
					const number = doE164(num);
					if (number && user?.user) {
						const callObject = {
							userId: number?.formatted?.substring(1),
							callerUserId: user.user.userId,
							videoSessionId: null,
							video: false,
							selectedDomain: selectedDomain
						};


						switch (currentState) {
							case "Established":
								// Only send CallStart if not already sent
								if (!apiCallStates.current.callStartSent) {
									webSocketApiService.SendVoipPushNotification({
										...callObject,
										callAction: "CallStart",
									});

									// Mark as sent and record timestamps
									apiCallStates.current.callStartSent = true;
									apiCallStates.current.callEndSent = false;
									apiCallStates.current.callStartTimestamp = Date.now();
									apiCallStates.current.callDurationStart = performance.now();

									setDuration(performance.now());
									setCallStartTime(Date.now());
									setShowNumberPlate(true);
								}
								break;

								case "Terminated":
									await handleTerminatedCallState(number);
									break;

							default:
								break;
						}
					}
				}
			} else if (phoneState !== null) {
				// No sessions but we had a phone state - reset
				resetCallStates();
			}
		} catch (error) {
			console.error('Error monitoring phone state:', error);
		}
		}, [phoneState, fullPhoneNumber, user, callStartTime, selectedDomain, resetCallStates, getSessionState, handleTerminatedCallState]);

	const makeCall = useCallback(async (phoneNumberToCall = null) => {
		if (callTriggered) return;

		try {
			await checkOnline();

			// Use passed number or fallback to state
			let numberToUse = phoneNumberToCall || fullPhoneNumber;
			console.log('Making call with number:', numberToUse);

			// Add safety check for valid phone number
			if (!numberToUse || numberToUse === "+") {
				console.error('Cannot make call: phone number not set properly');
				return;
			}
			const number = doE164(numberToUse);
			console.log('Formatted number for call:', number, user.user?.isPostpaidUser, user?.balance);
			if (!user?.user?.isPostpaidUser) {
				if (user?.balance <= 0) {
					onClose();
					noBalance();
					return;
				}
			}

			if (number && number.formatted) {
				
				const phoneStoreState = phoneStore.getState();
				const sipAccount = phoneStoreState?.sipAccounts?.sipAccount;
				console.log('sipAccount', sipAccount);
				if (sipAccount) {
					console.log('Making call to:', number.formatted.substring(1));
					setCallTriggered(true);

					// Reset API call states before making new call
					apiCallStates.current = {
						callStartSent: false,
						callEndSent: false,
						callSummarySent: false,
						lastProcessedState: null,
						callStartTimestamp: null,
						callDurationStart: null
					};

					sipAccount.makeCall(number.formatted.substring(1));

					// Start monitoring phone state - clear any existing interval first
					if (phoneStateInterval.current) {
						clearInterval(phoneStateInterval.current);
					}
					phoneStateInterval.current = setInterval(monitorPhoneState, 500);
				} else {
					console.error('SIP account not available');
				}
			} else {
				console.error('Invalid phone number:', numberToUse);
			}
		} catch (error) {
			console.error('Error making call:', error);
			setCallTriggered(false);
		}
	}, [callTriggered, checkOnline, fullPhoneNumber, user, onClose, noBalance, monitorPhoneState]);

	const clearText = useCallback(() => {
		try {
			if (fullPhoneNumber.length <= 1) {
				setFullPhoneNumber("+");
				setCountry(null);
				cursor.current = 1;
			} else {
				// Remove last character
				let newNumber = fullPhoneNumber.substring(0, fullPhoneNumber.length - 1);

				if (newNumber.length <= 1) {
					newNumber = "+";
					setCountry(null);
					cursor.current = 1;
				} else {
					// Check if country should be updated after removal
					const foundCountry = findCountryByDialCode(newNumber);
					if (foundCountry !== country) {
						setCountry(foundCountry);
					}
					cursor.current = newNumber.length;
				}

				setFullPhoneNumber(newNumber);
			}
		} catch (error) {
			console.error('Error clearing text:', error);
		}
	}, [fullPhoneNumber, country, findCountryByDialCode]);

	// Initialize component with caller info or phone number from link
	useEffect(() => {
		console.log('callerInfo', callerInfo)
		try {
			if (callerInfo) {
				const { toNumber } = callerInfo;
				let number = toNumber.indexOf("+") !== -1 ? toNumber : "+" + toNumber;
				number = formatPhoneNumber(null, number);
				const foundCountry = findCountryByDialCode(number);
				number = number.replace(/\s/g, '');
				if (foundCountry) {
					setCountry(foundCountry);
				}
				setShowNumberPlate(false);
				setFullPhoneNumber(number);
				setIsInitialized(true);
				// Pass the number directly to makeCall instead of relying on state
				setTimeout(() => {
					console.log('About to make auto-call with number:', number);
					makeCall(number); // Pass the number directly
				}, 1200);
				const now = Date.now();
				dispatch(getLoggedInUserCallHistory(now));
			} else if (phoneNumFromLink) {
				console.log('phoneNumFromLink', phoneNumFromLink);
				const toNumber = phoneNumFromLink;
				let number = toNumber.indexOf("+") !== -1 ? toNumber : "+" + toNumber;
				number = formatPhoneNumber(null, number);
				const foundCountry = findCountryByDialCode(number);

				if (foundCountry) {
					setCountry(foundCountry);
				}

				if (number) {
					setTimeout(() => {
						setFullPhoneNumber(number);
						setIsInitialized(true);
					}, 200);
				}
			} else {
				setIsInitialized(true);
			}
		} catch (error) {
			console.error('Error in initialization useEffect:', error);
			setIsInitialized(true);
		}
	}, []); // Removed makeCall from dependencies to prevent infinite loop

	// Handle cursor positioning and UI updates
useEffect(() => {
  if (!isInitialized) return;

  setCursorPositions();

  if (phoneState !== "Establishing") return;

  let attempts = 0;

  const interval = setInterval(() => {
    try {
      const root = document.querySelector(".react-sip-phone");
      if (!root) return;

      // Get all images inside SIP phone
      const images = root.querySelectorAll("button img, div img");

      if (images.length >= 3) {
        // Assign safely by index
        if (images[0]) images[0].src = "/img/call_end-24px.svg";
        if (images[1]) images[1].src = "/img/phone_paused-24px.svg";
        if (images[2]) images[2].src = "/img/mic_off-24px.svg";

        clearInterval(interval);
      }

      attempts++;
      if (attempts > 30) {
        clearInterval(interval); // stop after ~3 seconds
      }
    } catch (error) {
      console.warn("Error updating UI elements:", error);
      clearInterval(interval);
    }
  }, 100);

  return () => clearInterval(interval);
}, [phoneState, isInitialized, setCursorPositions]);


	// Cleanup on unmount
		useEffect(() => {
			return () => {
				if (phoneStateInterval.current) {
					clearInterval(phoneStateInterval.current);
				}
				if (offlineDropTimeoutRef.current) {
					clearTimeout(offlineDropTimeoutRef.current);
					offlineDropTimeoutRef.current = null;
				}
			};
		}, []);

	useEffect(() => {
		const handleOffline = () => {
			console.log("handleOffline");
			markRecoveryPendingIfCallActive();
			if (offlineDropTimeoutRef.current) {
				clearTimeout(offlineDropTimeoutRef.current);
			}
			offlineDropTimeoutRef.current = setTimeout(() => {
				if (isUserOnline()) {
					return;
				}
				const session = getCurrentSession();
				const currentState = getSessionState(session);
				if (session && (currentState === "Established" || currentState === "Establishing")) {
					forceDropCallAfterRecoveryFailure(session);
				}
			}, RECOVERY_TIMEOUT_MS);
		};

		const triggerRecovery = () => {
				console.log("handleOnline");
			if (offlineDropTimeoutRef.current) {
				clearTimeout(offlineDropTimeoutRef.current);
				offlineDropTimeoutRef.current = null;
			}
			recoveryPendingRef.current = true;
			beginRecoveryWindow();
			checkOnline()
				.then(() => {
					setTimeout(() => {
						recoverCallMediaAfterReconnect();
					}, 600);
				})
				.catch(() => {});
		};

		window.addEventListener("offline", handleOffline);
		window.addEventListener("online", triggerRecovery);
		window.addEventListener("stream_closed", triggerRecovery);

			return () => {
				window.removeEventListener("offline", handleOffline);
				window.removeEventListener("online", triggerRecovery);
				window.removeEventListener("stream_closed", triggerRecovery);
				if (offlineDropTimeoutRef.current) {
					clearTimeout(offlineDropTimeoutRef.current);
					offlineDropTimeoutRef.current = null;
				}
			};
	}, [checkOnline, markRecoveryPendingIfCallActive, recoverCallMediaAfterReconnect, beginRecoveryWindow, getCurrentSession, getSessionState, forceDropCallAfterRecoveryFailure]);

	const forceDisconnectSip = () => {
	try {
		const phoneStoreState = phoneStore.getState();
		const sipAccount = phoneStoreState?.sipAccounts?.sipAccount;

		if (sipAccount?._transport?.disconnect) {
			sipAccount._transport.disconnect();
		}
	} catch (e) {
		console.warn("Error disconnecting SIP transport:", e);
	}
};
const destroyPeerConnection = (session) => {
	try {
		const pc = session?.sessionDescriptionHandler?.peerConnection;
		if (pc) {
			pc.getSenders()?.forEach(s => s.track?.stop());
			pc.getReceivers()?.forEach(r => r.track?.stop());
			pc.close();
		}
	} catch (e) {
		console.warn("Error destroying peer connection:", e);
	}
};
		useEffect(() => {
			const interval = setInterval(() => {
				try {
					const session = getCurrentSession();
					const currentState = getSessionState(session);
				if (!session || (currentState !== "Established" && currentState !== "Establishing")) {
					return;
				}

					const peerConnection = session?.sessionDescriptionHandler?.peerConnection;
					const { connectionUnhealthy, hasLiveRemoteAudioTrack } = getConnectionRecoveryFlags(peerConnection);
					const needsMediaRecovery =
						connectionUnhealthy ||
						(currentState === "Established" && !hasLiveRemoteAudioTrack);
					const online = isUserOnline();

					if (needsMediaRecovery || !online) {
						recoveryPendingRef.current = true;
						beginRecoveryWindow();
					} else if (recoveryPendingRef.current) {
						recoveryPendingRef.current = false;
						clearRecoveryWindow();
					}

					if (recoveryPendingRef.current) {
						if (
							recoveryDeadlineRef.current &&
							Date.now() >= recoveryDeadlineRef.current
						) {
							forceDropCallAfterRecoveryFailure(session);
							return;
						}
						if (!online) {
							return;
						}
						recoverCallMediaAfterReconnect();
					}
				} catch (error) {
					console.warn("Call recovery watchdog error:", error);
				}
			}, 2000);

			return () => clearInterval(interval);
		}, [getCurrentSession, getSessionState, recoverCallMediaAfterReconnect, getConnectionRecoveryFlags, beginRecoveryWindow, clearRecoveryWindow, forceDropCallAfterRecoveryFailure]);

	const userDomain = selectedDomain || "";

	if (!isInitialized) {
		return <div>Loading...</div>;
	}

	return (
		<div style={{ borderRadius: "0.3rem", overflow: "hidden" }}>
			<div style={{ backgroundColor: "#2FC76F" }}>
				<div className="flex justify-center pt-8">
					<CountrySelector
						onChange={selectCountry}
						selectedCountry={country}
						className=""
						style={{
							background: "transparent",
							border: "none",
							color: "#fff",
							webkitAppearance: "auto",
						}}
					/>
				</div>

				<div className="p-5">
					<div style={{ textAlign: "center" }}>
						<input
							name="value"
							type="tel"
							className="hidden-input"
							style={{
								fontSize: "30px",
								lineHeight: "36px",
								color: "#fff",
								textAlign: "center",
							}}
							value={fullPhoneNumber}
							onChange={handlePhoneNumberChange}
							onKeyDown={handleKeyDown}
							onBlur={() => {
								if (!callerInfo) {
									try {
										const formatted = formatPhoneNumber(null, fullPhoneNumber);
										setFullPhoneNumber(formatted);
									} catch (error) {
										console.error('Error formatting phone number on blur:', error);
									}
								}
							}}
							maxLength="20"
							autoFocus
							ref={phnInput}
							placeholder="Enter phone number"
						/>
					</div>
				</div>
			</div>

			<div className={phoneState === null ? "hidden" : "block react-sip-phone"}>
				<ReactSipPhone
					name={name || ""}
					sipCredentials={{
						sipuri: sipuri || "",
						password: password || "",
					}}
					sipConfig={{
						websocket: websocket || "",
						defaultCountryCode: "",
					}}
					phoneConfig={{
						disabledButtons: disabledButtons || [],
						disabledFeatures: disabledFeatures || [],
						defaultDial: "",
						sessionsLimit: 3,
						attendedTransferLimit: 2,
						autoAnswer: false,
					}}
					appConfig={{
						mode: mode || "",
						started: false,
						appSize: "large",
					}}
					width={0}
				/>
			</div>

			{showNumberPlate && (
				<div
					className={`pad ${userDomain}_font`}
					style={{
						display: phoneState === "Terminated" ? "none" : "block",
					}}
				>
					<NumberPad onClick={addNumber} />

					{phoneState === null && (
						<div className="flex justify-between items-center my-3 px-4">
							<a onClick={onClose} className={`primary-link cursor-pointer ${userDomain}_font`}>
								Close
							</a>
							<a
								className={`flex justify-center items-center btn btn-success btn-icon-o btn-sm btn-rounded mb-2 ${userDomain}_font`}
								style={{
									height: "60px",
									width: "60px",
								}}
								onClick={() => {
									if (reduxState?.balance <= 0) {
										openRecharge()
										onClose()
										showToast('error', `Insufficient balance.`)
									} else {
										makeCall()
									}
								}} // Fixed: Use arrow function instead of calling makeCall immediately
							>
								<img
									src="/img/call-receivecall-btn@2x.png"
									className="w-[40px] h-[40px]"
									alt="call-receivecall-btn@2x.png"
								/>
							</a>
							<a onClick={clearText} className={`secodary-link cursor-pointer ${userDomain}_font`}>
								Clear
							</a>
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default DiallerKeyPad;
